import { useEffect, useReducer } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ContentBlock, IMessage, StreamingEvent } from '../models/Message';
import {
    applyStreamingEvent,
    initialStreamingState,
    LiveBlock,
} from './streamingReducer';

export interface UseStreamingMessageOptions {
    message: IMessage;
    /** The shared WebSocket created in Workspaces.tsx. */
    socket: WebSocket | null;
    collaborationId: string;
    /** Only activates when true — set to `message.status === 'streaming'`. */
    enabled: boolean;
}

export interface UseStreamingMessageResult {
    contentBlocks: ContentBlock[];
    isStreaming: boolean;
    isDone: boolean;
    error: string | null;
}

/**
 * Manages the full lifecycle of a single streaming LLM response:
 *
 *  1. Joins `chat:{session_id}` group on the shared WebSocket
 *  2. Dispatches incoming block events to the reducer
 *  3. On `done` → strips transient flags → writes final blocks to React Query cache
 *  4. Leaves the PubSub group
 *  5. 90 s timeout watchdog for stuck/crashed streams
 */
export function useStreamingMessage({
    message,
    socket,
    collaborationId,
    enabled,
}: UseStreamingMessageOptions): UseStreamingMessageResult {
    const queryClient = useQueryClient();

    const [state, dispatch] = useReducer(applyStreamingEvent, initialStreamingState);

    const groupName = `chat:${message.session_id}`;

    // ── Step 1: Join the PubSub session group ────────────────────────────────
    useEffect(() => {
        if (!enabled || !socket || !message.session_id) return;

        const join = () => {
            if (socket.readyState === WebSocket.OPEN) {
                socket.send(JSON.stringify({ type: 'joinGroup', group: groupName }));
            }
        };

        join();
        socket.addEventListener('open', join);
        return () => socket.removeEventListener('open', join);
    }, [enabled, socket, groupName, message.session_id]);

    // ── Step 2: Listen for block events from this session group ──────────────
    useEffect(() => {
        if (!enabled || !socket) return;

        const handleMessage = (e: MessageEvent) => {
            try {
                const outer = JSON.parse(e.data);
                // Only process messages addressed to our specific session group
                if (outer.type !== 'message' || outer.group !== groupName) return;

                const event: StreamingEvent =
                    typeof outer.data === 'string' ? JSON.parse(outer.data) : outer.data;
                dispatch(event);
            } catch {
                // Ignore malformed events
            }
        };

        socket.addEventListener('message', handleMessage);
        return () => socket.removeEventListener('message', handleMessage);
    }, [enabled, socket, groupName]);

    // ── Step 3 & 4: On done → finalize cache → leave group ──────────────────
    useEffect(() => {
        if (!state.isDone) return;

        // Strip transient streaming flags before committing to the cache
        const finalBlocks = (state.blocks as LiveBlock[])
            .filter((b) => b && !b._removed)
            .map(({ _streaming: _s, _removed: _r, _nextSeq: _n, _pendingChunks: _p, ...rest }: any) => rest as ContentBlock);

        queryClient.setQueryData<IMessage[]>(
            ['conversationMessages', collaborationId],
            (old) =>
                old?.map((m) =>
                    m.id === message.id
                        ? { ...m, contentBlocks: finalBlocks, status: 'active', isStreaming: false }
                        : m
                )
        );

        if (socket?.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'leaveGroup', group: groupName }));
        }
    }, [state.isDone]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Step 5: 90 s timeout watchdog ────────────────────────────────────────
    useEffect(() => {
        if (!enabled || state.isDone) return;
        const t = setTimeout(
            () => dispatch({ type: 'error', message: 'Stream timed out' }),
            90_000
        );
        return () => clearTimeout(t);
    }, [enabled, state.isDone]);

    return {
        contentBlocks: (state.blocks as LiveBlock[]).filter((b) => b && !b._removed) as ContentBlock[],
        isStreaming: enabled && !state.isDone,
        isDone: state.isDone,
        error: state.error,
    };
}
